import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { Label } from "@/components/ui/label";
import { BrandSelect } from "@/components/features/brands/BrandSelect";
import { brandKeys } from "@/lib/queryKeys";
import { listBrands } from "@/services/brandService";

export interface BrandSelectFieldProps {
  /** Brand id, or "" for none. */
  value: string;
  onChange: (brandId: string) => void;
  disabled?: boolean;
}

/**
 * Labelled default-marca field for the client forms.
 *
 * The label and help text live here rather than in each form so the create and
 * edit pages cannot describe the same setting differently — the two client
 * forms are separate components and already drift easily.
 *
 * Renders nothing for a tenant with no marcas, so the forms are untouched for
 * everyone who never uses the feature.
 */
export function BrandSelectField({
  value,
  onChange,
  disabled,
}: BrandSelectFieldProps): JSX.Element | null {
  const { t } = useTranslation();
  const { data: brands = [] } = useQuery({
    queryKey: brandKeys.list(),
    queryFn: listBrands,
  });
  if (brands.length === 0) return null;

  return (
    <div className="space-y-2">
      <Label htmlFor="defaultBrandId">
        {t("clients.form.defaultBrandLabel", "Marca por defecto")}
      </Label>
      <BrandSelect
        id="defaultBrandId"
        value={value || null}
        onChange={(v) => onChange(v ?? "")}
        disabled={disabled}
      />
      <p className="text-xs text-muted-foreground">
        {t(
          "clients.form.defaultBrandHelp",
          "Las facturas nuevas a este cliente salen con esta marca. No cambia las ya emitidas."
        )}
      </p>
    </div>
  );
}
